import React, { useEffect, useState } from "react"
import PropTypes from "prop-types"
import ModalForm from "../../../../components/compounds/modalForm/ModalForm.compunds"
import { ReactComponent as PencilIcon } from "../../../../design/assests/images/icons/pencil.svg"
import {
  updateVacationRequest,
  getTeamMemberDisabledDaysInRangeExcludeVacationID,
  getTeamMemberVacationResumeExcludeVacationID,
} from "../../../../services/vacation.service"
import useToast from "../../../../hooks/useToast.hook"
import { useSession } from "../../../../hooks/auth/useSession.hook"
import {
  MY_VACATION_MODULE_DEPLOY_REQUEST_SUMMARY_DATE,
  MY_VACATION_UPDATE_SERVICE_TYPES,
  VACATIONS_CHANGE_APPROVED_REQUEST_SUCCESS,
  VACATIONS_SAVE_ERROR_MESSAGE,
} from "../../../../consts/modules/vacations.consts"
import VacationCreateRequestSummary from "../../components/VacationCreateRequestSummary.molecule"
import { DateTime } from "luxon"
import _ from "lodash"
import useMyVacationRequestFormState, {
  VACATION_REQUEST_FORM_MODE,
} from "../../../../hooks/useMyVacationRequestFormState.hook"
import useMyVacationRequestMaxDate from "../../../../hooks/useVacationMaxDate.hook"
import {
  getNumberOfTypeDaysToUse,
  getTeamMembersEndOfPeriodMonth,
  getTeamMembersStartOfPeriodMonth,
  isMexicanSchema,
} from "../../../../utils/vacation.utils"
import { compareDates } from "../../../../utils/date.utils"

const ModalChangeMyVacationApprovedRequest = ({
  visible,
  setVisible,
  selectedVacationRequest,
  setSelectedVacationRequest,
  selectedPeriod,
  triggerRefresh,
  holidaySchemaId,
}) => {
  // Form States
  const {
    // States
    formData,
    setFormData,
    validations,
    setValidations,
    availableDaysList,
    setAvailableDaysList,
    disabledDayList,
    setDisabledDayList,
    bucketDaysList,
    plusDaysList,
    multipleDays,
    setMultipleDays,
    daysToUse,
    typesOfDaysToUse,
    setTypesOfDaysToUse,
    disabledSaveButton,
    allowFormStateChanges,
    setAllowFormStateChanges,
    loading,
    anniversaryDate,

    // Functions
    updateDaysToUse,
    resetForms,

    // Services
    updateTeamMemberBucketDaysExcludeVacationId,
    updateAvailableBucketDaysBasedOnPeriodChange,
    updateAvailablePlusDaysBasedOnPeriodChange,
    updateDisabledDayListExcludeVacationId,
    updateAvailableDaysListExcludeVacationId,
    updateTeamMemberPlusDaysExcludeVacationId,
  } = useMyVacationRequestFormState({
    holidaySchemaId,
    vacationRequestFormMode: VACATION_REQUEST_FORM_MODE.EDIT_APPROVED,
  })
  const { maxDate, updateMaxDate } = useMyVacationRequestMaxDate()
  const [submitting, setSubmitting] = useState(false)

  // Hooks
  const { setSuccessMessage, setErrorMessage } = useToast()
  const { user } = useSession()

  useEffect(() => {
    if (visible) {
      const initModalForm = async () => {
        const startDate = DateTime.max(
          DateTime.fromISO(selectedPeriod[0]),
          MY_VACATION_MODULE_DEPLOY_REQUEST_SUMMARY_DATE
        )
        await updateAvailableDaysListExcludeVacationId(
          selectedPeriod[0],
          selectedPeriod[1],
          holidaySchemaId,
          selectedVacationRequest?.idVacations
        )
        await updateDisabledDayListExcludeVacationId(
          selectedPeriod[0],
          selectedPeriod[1].plus({ years: 10 }),
          selectedVacationRequest?.idVacations
        )
        await updateTeamMemberBucketDaysExcludeVacationId(
          startDate,
          selectedPeriod[1],
          holidaySchemaId,
          selectedVacationRequest?.idVacations,
          selectedVacationRequest?.startDate
        )
        await updateTeamMemberPlusDaysExcludeVacationId(
          selectedPeriod[0],
          selectedPeriod[1],
          holidaySchemaId,
          selectedVacationRequest?.idVacations,
          selectedVacationRequest?.startDate
        )
      }
      initModalForm()
      setAllowFormStateChanges(true)
    }
  }, [visible])

  useEffect(() => {
    if (allowFormStateChanges) {
      updateDaysToUse(
        formData.requestDates[0],
        formData.requestDates[1],
        disabledDayList
      )
      updateMaxDate(
        formData.requestDates[0],
        holidaySchemaId && isMexicanSchema(holidaySchemaId) ?
          availableDaysList.bucketDays + 3
        : 0,
        availableDaysList.plusDays,
        availableDaysList.accordedDays,
        availableDaysList.additionalDays,
        disabledDayList
      )
      if (
        holidaySchemaId &&
        isMexicanSchema(holidaySchemaId) &&
        bucketDaysList
      ) {
        updateAvailableBucketDaysBasedOnPeriodChange(
          formData.requestDates[0],
          formData.requestDates[1],
          bucketDaysList || {}
        )
        updateAvailablePlusDaysBasedOnPeriodChange(
          formData.requestDates[0],
          formData.requestDates[1],
          plusDaysList || {}
        )
      }
    }
  }, [formData.requestDates, allowFormStateChanges])

  useEffect(() => {
    if (visible && !_.isEmpty(selectedVacationRequest)) {
      defineNewFormData(selectedVacationRequest)
    }
  }, [visible, selectedVacationRequest])

  const defineNewFormData = (newVacationRequest) => {
    if (newVacationRequest) {
      setFormData({
        requestDates: [
          DateTime.fromSQL(newVacationRequest.startDate.split("T")[0]).set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
          DateTime.fromSQL(newVacationRequest.endDate.split("T")[0]).set({
            hour: 12,
            millisecond: 0,
            minute: 0,
            second: 0,
          }),
        ],
        bucketDays: newVacationRequest.numberOfBucketDays || 0,
        plusDays: newVacationRequest.numberOfPlusDays || 0,
        accordedDays: newVacationRequest.numberOfAccordedDays || 0,
        additionalDays: newVacationRequest.numberOfAdditionalDays || 0,
        motive: newVacationRequest.vacationsMotive || "",
        changeMotive: "",
      })
      setMultipleDays(
        !compareDates(
          DateTime.fromSQL(newVacationRequest.startDate.split("T")[0]),
          DateTime.fromSQL(newVacationRequest.endDate.split("T")[0])
        )
      )
      updateRestOfFormDateOnNewVacationRequest(
        selectedVacationRequest.id,
        DateTime.fromSQL(newVacationRequest.startDate.split("T")[0]).set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        DateTime.fromSQL(newVacationRequest.endDate.split("T")[0]).set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        })
      )
    } else {
      resetForms()
    }
  }

  const updateRestOfFormDateOnNewVacationRequest = async (
    vacationRequestID,
    startDate,
    endDate
  ) => {
    try {
      const today = DateTime.now()
      const response01 = await getTeamMemberVacationResumeExcludeVacationID(
        today.startOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        today.endOf("month").set({
          hour: 12,
          millisecond: 0,
          minute: 0,
          second: 0,
        }),
        getTeamMembersStartOfPeriodMonth(user?.workAnniversary, selectedPeriod[0]),
        getTeamMembersEndOfPeriodMonth(user?.workAnniversary, selectedPeriod[1]),
        vacationRequestID
      )
      if (!response01.success) {
        throw new Error(response01.message)
      }
      const teamMemberResume = response01.result
      const typesOfDaysToUse = getNumberOfTypeDaysToUse({
        holidaySchemaId,
        teamMemberVacationResume: teamMemberResume,
        availableBucketDays: teamMemberResume.availableBucketDays,
      })
      const vacationRequestAvailableDayList = {
        bucketDays: teamMemberResume.availableBucketDays,
        plusDays: teamMemberResume.availablePlusDays,
        accordedDays: teamMemberResume.availableAccordedDays,
        additionalDays: teamMemberResume.availableAdditionalDays,
      }

      setTypesOfDaysToUse(typesOfDaysToUse)
      setAvailableDaysList(vacationRequestAvailableDayList)

      // ---------------------

      const response02 =
        await getTeamMemberDisabledDaysInRangeExcludeVacationID(
          selectedPeriod[0],
          selectedPeriod[1],
          vacationRequestID
        )
      if (!response02.success) {
        throw new Error(response02.message)
      }
      const vacationRequestDisabledDayList = response02.result.map((day) => {
        return { date: day.date.split("T")[0] }
      })
      setDisabledDayList(vacationRequestDisabledDayList)

      // ---------------------

      updateDaysToUse(startDate, endDate, vacationRequestDisabledDayList)
      updateMaxDate(
        startDate,
        vacationRequestAvailableDayList.bucketDays,
        vacationRequestAvailableDayList.plusDays,
        vacationRequestAvailableDayList.accordedDays,
        vacationRequestAvailableDayList.additionalDays,
        vacationRequestDisabledDayList
      )
    } catch (error) {
      setErrorMessage({
        title: "Error",
        message: error.message,
      })
    }
  }

  useEffect(() => {
    updateAvailableBucketDaysBasedOnPeriodChange(
      formData.requestDates[0],
      maxDate,
      bucketDaysList || {}
    )
  }, [maxDate])

  const handleSubmit = async () => {
    setSubmitting(true)
    try {
      const response = await updateVacationRequest(
        selectedVacationRequest.id,
        MY_VACATION_UPDATE_SERVICE_TYPES.APPROVED,
        formData,
        selectedPeriod
      )
      if (!response.success) {
        throw new Error(response.message)
      }
      setVisible("")
      setAllowFormStateChanges(false)
      setSelectedVacationRequest(null)
      triggerRefresh()
      setSuccessMessage(VACATIONS_CHANGE_APPROVED_REQUEST_SUCCESS)
    } catch (error) {
      setErrorMessage(VACATIONS_SAVE_ERROR_MESSAGE)
    }
    setSubmitting(false)
  }

  const handleModalSetVisibility = (visibleModal) => {
    if (!visibleModal) {
      setVisible("")
      setAllowFormStateChanges(false)
      setSelectedVacationRequest(null)
    }
  }

  return (
    <ModalForm
      className="my-vacation-create-modal-form"
      visible={visible}
      setVisible={handleModalSetVisibility}
      headerTitle="Editar vacaciones"
      headerIcon={<PencilIcon />}
      dismissableMask
      closable
      saveLabel="Guardar"
      disabledSaveBtn={submitting || disabledSaveButton}
      onSave={handleSubmit}
    >
      <VacationCreateRequestSummary
        multipleDays={multipleDays}
        setMultipleDays={setMultipleDays}
        daysToUse={daysToUse}
        typesOfDaysToUse={typesOfDaysToUse}
        isApprovedChangeRequest
        maxDate={maxDate}
        vacationRequestValues={formData}
        setVacationRequestValues={setFormData}
        vacationRequestValidation={validations}
        setVacationsRequestValidation={setValidations}
        // selectedPeriod={selectedPeriod}
        selectedPeriod={[
          DateTime.max(
            DateTime.fromISO(selectedPeriod[0]),
            MY_VACATION_MODULE_DEPLOY_REQUEST_SUMMARY_DATE
          ),
          DateTime.fromISO(selectedPeriod[1]),
        ]}
        availableDaysList={availableDaysList}
        disabledDayList={disabledDayList}
        reminderTitle="¿Cambio de planes?"
        reminderBody="Tus modificaciones serán enviadas al administrador para su aprobación, mientras tanto tu solicitud sigue pendiente."
        holidaySchemaId={holidaySchemaId}
        bucketDaysList={bucketDaysList}
        vacationFormMode={VACATION_REQUEST_FORM_MODE.EDIT_APPROVED}
        disabled={loading}
        anniversaryDate={anniversaryDate}
      />
    </ModalForm>
  )
}

export default ModalChangeMyVacationApprovedRequest

ModalChangeMyVacationApprovedRequest.propTypes = {
  visible: PropTypes.bool.isRequired,
  setVisible: PropTypes.func.isRequired,
  selectedVacationRequest: PropTypes.object,
  triggerRefresh: PropTypes.func,
}
